import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { isUndefined } from 'lodash';
import { map } from 'rxjs/operators';
import { isPointCloudOutput } from '../../../../../../../cross-team-shared/misc-utils/output-utils';
import { VideoStore } from '../../../../../../../outputs/annotation/video/state/video-store';
import { EventTrackingService } from '../../../../../../../shared/event-tracking/event-tracking.service';
import { getOutputByName } from '../../../../../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { outputInfoStoreToken } from '../../../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../../h3ml/state/task-workspace-store';
import { splitViewIcon } from '../../../../../../shared/icons/toolbars/split-view-icon';
import { FusionManager } from '../../../fusion/fusion-manager';

@Component({
  selector: 'h3-toggle-split-view-button',
  templateUrl: './toggle-split-view-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ToggleSplitViewButtonComponent {
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);

  isInSplitView$ = this.taskWorkspaceStore('splitViewOutputName').$.pipe(
    map(isDefined),
  );
  splitViewIcon = splitViewIcon;
  tooltipText$ = this.isInSplitView$.pipe(
    map((isInSplitView) =>
      isInSplitView ? 'Exit split-screen' : 'Split-screen',
    ),
  );

  private eventTrackingService = inject(EventTrackingService);

  constructor(private injector: Injector) {}

  toggleSplitView() {
    const { splitViewOutputName } = this.taskWorkspaceStore.state();

    const newSplitViewOutputName = splitViewOutputName
      ? undefined
      : this.getSplitViewOutputName();
    this.taskWorkspaceStore('splitViewOutputName').set(newSplitViewOutputName);
    if (isUndefined(newSplitViewOutputName)) {
      this.disableSplitViewOnlySettings();
    }

    this.trackEvent(newSplitViewOutputName);

    if (newSplitViewOutputName) {
      const videoOutput = getOutputByName(
        this.taskWorkspaceStore.state().project,
        newSplitViewOutputName,
      );
      // limit to 2d mode in split view
      (
        this.injector.get(FusionManager).getMinion(videoOutput.id)!
          .store as VideoStore
      )('redirectShortcutsToPointCloud').set(false);
    }
  }

  private getSplitViewOutputName() {
    const outputInfoStore = this.injector.get(outputInfoStoreToken);
    const output = outputInfoStore.state().output;
    if (isPointCloudOutput(output)) {
      return this.taskWorkspaceStore.getFirstAvailableVideoOutput();
    }
    return output.name;
  }

  private trackEvent(newSplitViewOutputName: string | undefined) {
    this.eventTrackingService.track({
      id: 'toggle_split_view',
      bucket: 'navigation',
      sub_bucket: 'frame',
      active: isDefined(newSplitViewOutputName),
      source: 'button',
    });
  }

  private disableSplitViewOnlySettings() {
    this.taskWorkspaceStore('settings')('regionOfInterestSettings')(
      'useForAllCameras',
    ).set(true);
  }
}
